import React, { useState } from 'react';
import {
    CompetitionType,
    EventType,
} from 'src/domains/layouts/webview/components/promotedSports/PromotedEventsState.state';
import { observer } from 'src/utils/mobx-react';
import { EventRowMobxWrapper } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowMobx';
import { EventRow } from 'src/domains/sportsbook/webview/components/eventRow/EventRow';
import { EventRowNFL } from 'src/domains/sportsbook/shared/Components';
import { EventRowMLB } from 'src/domains/sportsbook/webview/components/eventsList/eventRowMLB/EventRowMLB';
import { EventRowNBA } from 'src/domains/sportsbook/webview/components/eventsList/eventRowNBA/EventRowNBA';
import { EventRowNHL } from 'src/domains/sportsbook/webview/components/eventsList/eventRowNHL/EventRowNHL';
import { makeObservable, observable } from 'mobx';
import {
    getMarketAndSelections,
    PossibleMarketAndSelectionInListType,
} from 'src/domains/sportsbook/state/eventsListState/eventsGroup/getMarketAndSelections';
import { SELECTIONS_LABELS } from 'src/domains/sportsbook/state/eventsListState/eventsGroup/EventsGroupView';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { EventGroupHeaderMLB } from 'src/domains/sportsbook/webview/components/eventsList/EventGroupHeaderMLB';
import { EventGroupHeaderNBA } from 'src/domains/sportsbook/webview/components/eventsList/EventGroupHeaderNBA';
import { EventGroupHeaderNFL } from 'src/domains/sportsbook/webview/components/eventsList/EventGroupHeaderNFL';
import { EventGroupHeaderNHL } from 'src/domains/sportsbook/webview/components/eventsList/EventGroupHeaderNHL';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { checkCompetition } from 'src/domains/sportsbook/utils/checkCompetition';
import { CompetitionHeader } from 'src/domains/sportsbook/webview/components/competitionHeader/CompetitionHeader';
import { CompetitionWrapper } from './PromotedSportSection.style';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';

interface CompetitionDataPropsType {
    competition: CompetitionType;
    sportSlug: string;
}

const renderCompetitionView = (
    competitionId: CompetitionId,
    competitionName: string,
    event: EventListGroupEventItemType,
    sportSlug: string,
    possibleMarketAndSelectionInList: PossibleMarketAndSelectionInListType
): JSX.Element => {
    const mlbOrMajorCompetition = checkCompetition(competitionName, ['MLB', 'Major']);
    const id = event.id2;
    const template = event.template;

    if (mlbOrMajorCompetition === true && template.startsWith('two-participant')) {
        return (
            <EventRowMLB
                key={`event_${id.toOldId()}`}
                eventId={id}
                smallEvent={event}
            />
        );
    } else if (checkCompetition(competitionName, ['NHL']) === true && template.startsWith('two-participant')) {
        return (
            <EventRowNHL
                key={`event_${id.toOldId()}`}
                eventId={id}
                smallEvent={event}
            />
        );
    } else if (checkCompetition(competitionName, ['NBA']) === true && template.startsWith('two-participant')) {
        return (
            <EventRowNBA
                key={`event_${id.toOldId()}`}
                eventId={id}
                smallEvent={event}
            />
        );
    } else if (competitionName === 'US NFL' || competitionName === 'NFL Matches') {
        return (
            <EventRowNFL
                key={`event_${id.toOldId()}`}
                eventId={id}
                smallEvent={event}
            />
        );
    } else if (sportSlug === 'football') {
        return (
            <EventRowMobxWrapper
                key={`event_${id.toOldId()}`}
                smallEvent={event}
                competitionId={competitionId}
            />
        );
    } else {
        return (
            <EventRow
                key={`event_${event.id}`}
                sport={sportSlug}
                smallEvent={event}
                possibleMarketAndSelectionInList={possibleMarketAndSelectionInList}
                filterMarketsByPopularTag={false}
            />
        );
    }
};

const getSmallEvents = (events_list: EventType[]): Array<EventListGroupEventItemType> => {
    const result = [];
    for (const event of events_list) {
        const model = event.event_id.getEventModel();
        if (model !== null) {
            result.push(model.convertToSmallEvent());
        }
    }

    return result;
};

interface HeaderType {
    competition: CompetitionType;
    isOpenedCompetition: boolean;
    sportSlug: string;
    setIsOpened: () => void;
}

const Header = observer(
    'Header',
    ({ competition, isOpenedCompetition, sportSlug, setIsOpened }: HeaderType): JSX.Element => {
        const common = useCommon();
        const languagesState = LanguagesState.get(common);

        const competitionName = competition.competition_id.getModel()?.name ?? '';
        const smallEventsArray = getSmallEvents(competition.events_list);
        const possibleMarketAndSelectionInList = getMarketAndSelections(smallEventsArray);
        const selectionsLabels = SELECTIONS_LABELS(languagesState.getTranslation);
        const selections = possibleMarketAndSelectionInList.headers.map(
            (x) => selectionsLabels[`${sportSlug}:${x}`] ?? selectionsLabels[x] ?? x
        );
        const mlbOrMajorCompetition = checkCompetition(competitionName, ['MLB', 'Major']);
        const areEventsTwoParticipantsOnly = getSmallEvents(competition.events_list).every((smallEvent) =>
            smallEvent.template.startsWith('two-participant')
        );

        if (mlbOrMajorCompetition === true && areEventsTwoParticipantsOnly === true) {
            return (
                <EventGroupHeaderMLB
                    key={`sport_competition_${competition.competition_id.toOldId()}`}
                    label={competitionName}
                    onToggle={setIsOpened}
                    open={isOpenedCompetition}
                    dataTest='header-competitionLevel'
                />
            );
        } else if (checkCompetition(competitionName, ['NHL']) === true && areEventsTwoParticipantsOnly === true) {
            return (
                <EventGroupHeaderNHL
                    key={`sport_competition_${competition.competition_id.toOldId()}`}
                    label={competitionName}
                    onToggle={setIsOpened}
                    open={isOpenedCompetition}
                    dataTest='header-competitionLevel'
                />
            );
        } else if (checkCompetition(competitionName, ['NBA']) === true && areEventsTwoParticipantsOnly === true) {
            return (
                <EventGroupHeaderNBA
                    key={`sport_competition_${competition.competition_id.toOldId()}`}
                    label={competitionName}
                    onToggle={setIsOpened}
                    open={isOpenedCompetition}
                    dataTest='header-competitionLevel'
                />
            );
        } else if (competitionName === 'US NFL' || competitionName === 'NFL Matches') {
            return (
                <EventGroupHeaderNFL
                    dataTest='header-competitionLevel'
                    key={`sport_competition_${competition.competition_id.toOldId()}`}
                    label={competitionName}
                    onToggle={setIsOpened}
                    open={isOpenedCompetition}
                />
            );
        } else {
            return (
                <CompetitionHeader
                    selections={selections}
                    isOutrightEvent={false}
                    label={competitionName}
                    isOpen={isOpenedCompetition}
                    onToggle={setIsOpened}
                />
            );
        }
    }
);

class State {
    @observable public isOpenedCompetition: boolean = true;

    public constructor() {
        makeObservable(this);
    }

    public setIsOpened = (): void => {
        this.isOpenedCompetition = !this.isOpenedCompetition;
    };
}

export const CompetitionData = observer('CompetitionData', ({ competition, sportSlug }: CompetitionDataPropsType) => {
    const common = useCommon();
    const { promotedEventsState } = AppLayoutsState.get(common);
    const [state] = useState(() => new State());

    const configState = ConfigComponents.get(common).config;
    const competitionName = competition.competition_id.getModel()?.name ?? '';
    const smallEventsArray = getSmallEvents(competition.events_list);
    const possibleMarketAndSelectionInList = getMarketAndSelections(smallEventsArray);
    const eventsSorted = competition.events_list.sort((a, b) => a.display_order - b.display_order);
    const eventModels = eventsSorted
        .map((event) => event.event_id.getEventModel())
        .filter(
            (eventModel): eventModel is EventModel =>
                eventModel !== null &&
                promotedEventsState.isEventToShow(eventModel) &&
                eventModel.allMarketsForEventPage.length > 0
        );

    return eventModels.length > 0 ? (
        <React.Fragment key={competition.id}>
            <Header
                competition={competition}
                sportSlug={sportSlug}
                isOpenedCompetition={state.isOpenedCompetition}
                setIsOpened={state.setIsOpened}
            />
            {state.isOpenedCompetition ? (
                <CompetitionWrapper variant={configState.componentVariants.sportsbookVariant}>
                    {eventModels.map((eventModel) => {
                        const smallEvent = eventModel.convertToSmallEvent();

                        return renderCompetitionView(
                            competition.competition_id,
                            competitionName,
                            smallEvent,
                            sportSlug,
                            possibleMarketAndSelectionInList
                        );
                    })}
                </CompetitionWrapper>
            ) : null}
        </React.Fragment>
    ) : null;
});
